import React from 'react'
import {Grid, Tooltip, IconButton} from '@mui/material'
import {makeStyles} from '@oc/styles'
import {Forward} from '@mui/icons-material'
import {OcTransferDirection, OcTransferListTranslations} from './transferListConfig'
import {messages} from './translations'

const useStyles = makeStyles({
    icon: {
        padding: 2,
        height: 28,
        width: 28
    },
    rotateUp: {
        transform: 'rotate(270deg)'
    },
    rotateDown: {
        transform: 'rotate(90deg)'
    },
    rotateLeft: {
        transform: 'rotate(180deg)'
    }
})

interface ActionsCellRendererProps<T> {
    data: T
    transferDirection: OcTransferDirection
    indexOf: (item: T) => number
    maxIndex: number
    transfer?: (item: T) => void
    move?: (item: T, toIndex: number) => void
    translations?: OcTransferListTranslations
}

export function ActionsCellRenderer<T>(props: ActionsCellRendererProps<T>) {
    const {data, indexOf, maxIndex, transferDirection, transfer, move, translations} = props
    const {classes} = useStyles()

    const onTransfer = React.useCallback(() => transfer?.(data), [transfer, data])
    const onMoveUp = React.useCallback(() => {
        const index = indexOf(data)
        if (index > 0) {
            move?.(data, index - 1)
        }
    }, [move, indexOf, data])
    const onMoveDown = React.useCallback(() => {
        const index = indexOf(data)
        if (index < maxIndex) {
            move?.(data, index + 1)
        }
    }, [move, indexOf, maxIndex, data])

    return (
        <Grid container direction="row" alignItems="center" justifyContent="space-between">
            {transfer && (
                <Tooltip
                    title={
                        transferDirection === OcTransferDirection.Left
                            ? translations?.transferRight || messages.getText('buttons', 'transferRight')
                            : translations?.transferLeft || messages.getText('buttons', 'transferLeft')
                    }
                >
                    <IconButton data-cy="transfer-button" onClick={onTransfer} className={classes.icon} size="large">
                        {transferDirection === OcTransferDirection.Left ? (
                            <Forward className={classes.rotateLeft} />
                        ) : (
                            <Forward />
                        )}
                    </IconButton>
                </Tooltip>
            )}
            {move && (
                <>
                    <Tooltip title={translations?.moveUp || messages.getText('buttons', 'moveUp')}>
                        <IconButton data-cy="move-up-button" onClick={onMoveUp} className={classes.icon} size="large">
                            <Forward className={classes.rotateUp} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={translations?.moveDown || messages.getText('buttons', 'moveDown')}>
                        <IconButton
                            data-cy="move-down-button"
                            onClick={onMoveDown}
                            className={classes.icon}
                            size="large"
                        >
                            <Forward className={classes.rotateDown} />
                        </IconButton>
                    </Tooltip>
                </>
            )}
        </Grid>
    )
}
